import { requireCompositionBuildJob } from '../creationAdmission.js';
/**
 * BuildJobsRepo — typed repository over {@link DocStorePort} for
 * `buildJobs/{jobId}`, the builder work queue. A worker CLAIMS a queued job
 * (flip `queued → claimed` + stamp `workerId` + `heartbeatAt`), heartbeats
 * while it works, and completes or fails it.
 *
 * The claim is a race-free COMPARE-AND-SET via
 * {@link DocStorePort.guardedUpdate} (`status == 'queued'` precondition): two
 * concurrent workers (Cloud Tasks push + Cloud Run replicas) can never both win
 * a job — the loser's transaction sees `claimed` and returns false. The
 * Firestore rules keep `buildJobs` server-only, a second line of defense.
 */
import {
  PlatformError,
  type DocStorePort,
  type DocTxRunner,
  type QuerySpec,
  type ServerTimestampSentinel,
} from '@djgg/core';
import { commitCompositionFinalProof } from './compositionFinalProof.js';
import { commitCompositionProofBarrier, type CommitCompositionProofInput } from './compositionProofBarrier.js';
import { startCompositionWindow } from './compositionWindowStart.js';
import { canonicalJson } from '@djgg/gameplay-kit';
import { buildJobDoc, buildJobsCollection } from '../paths.js';
import type {
  BuildJob,
  MeasuredBuildCandidateV1,
  MeasuredBuildCheckpointV1,
  MeasuredBuildExecutionV1,
  MeasuredBuildOperationKind,
  MeasuredBuildReservationV1,
  MeasuredBuildTraceSegmentV1,
} from '../types.js';

export type BuildClaimAuthority = Pick<BuildJob, 'workerId' | 'claimToken' | 'leaseGeneration'>;

export interface ReserveMeasuredOperationInput {
  reservationId: string;
  operationId: string;
  kind: MeasuredBuildOperationKind;
  provider?: NonNullable<MeasuredBuildReservationV1['provider']>;
  model?: string;
  requestId?: string;
  calls: number;
  costMicros: number;
  maxDurationMs: number;
}

export function createMeasuredBuildExecution(
  acceptedAt: number,
  limits: MeasuredBuildExecutionV1['limits'],
  recipeRevision: 2 | 3 | 4 | 5 = 2,
): MeasuredBuildExecutionV1 {
  return {
    schemaVersion: 1,
    ledgerRevision: 0,
    recipe: { id: 'measured-first-playable', revision: recipeRevision } as MeasuredBuildExecutionV1['recipe'],
    acceptedAt,
    deadlineAt: acceptedAt + limits.maxWallTimeMs,
    limits: structuredClone(limits),
    usage: { reservedProviderCalls: 0, settledProviderCalls: 0, reservedCostMicros: 0, settledCostMicros: 0 },
    reservations: {},
    traceSegments: [],
    continuation: null,
  };
}

/** The durable evidence a measured build has retained: its current and best
 * candidates, every gate the current one has passed, and the committed
 * composition phase proofs. A checkpoint REVISION is not progress: re-saving
 * the same candidate with the same gate results bumps it. */
export function measuredCheckpointProgress(
  run: Pick<MeasuredBuildExecutionV1, 'checkpoint' | 'compositionBarrier' | 'compositionFinalProof'>,
): string {
  const checkpoint = run.checkpoint;
  const report = checkpoint?.candidates[checkpoint.currentCandidateId]?.report as {
    ok?: boolean; checks?: Record<string, { ran?: boolean; passed?: boolean | null }>;
  } & Record<string, unknown> | undefined;
  const passed = report ? [
    ...['typecheck', 'tests', 'build'].filter(name => (report[name] as { ok?: boolean } | undefined)?.ok === true),
    ...Object.entries(report.checks ?? {}).filter(([, check]) => check.ran === true && check.passed === true).map(([name]) => name),
  ].sort() : [];
  return canonicalJson({
    candidateId: checkpoint?.currentCandidateId ?? null,
    bestCandidateId: checkpoint?.bestCandidateId ?? null,
    ok: report?.ok === true,
    passed,
    structuralProof: run.compositionBarrier?.checkpointRevision ?? null,
    finalProof: run.compositionFinalProof?.checkpointRevision ?? null,
  });
}

/** Whether the retained evidence moved beyond what the latest continuation
 * resumed from. A request segment that yields, or a crashed claim that is
 * recovered, without it would only repeat the same work. */
export function measuredRunProgressed(run: MeasuredBuildExecutionV1): boolean {
  return !!run.checkpoint && run.continuation?.progress !== measuredCheckpointProgress(run);
}

function ledgerRevision(run: MeasuredBuildExecutionV1): number {
  return run.ledgerRevision ?? 0;
}

function requiresClaimGeneration(job: Pick<BuildJob, 'requiredWorkerCapability'>): boolean {
  return job.requiredWorkerCapability === 'verified-core-v2'
    || job.requiredWorkerCapability === 'composition-private-v2';
}

export function advanceLedger(
  run: MeasuredBuildExecutionV1,
  patch: Partial<MeasuredBuildExecutionV1>,
): MeasuredBuildExecutionV1 {
  return { ...run, ...patch, ledgerRevision: ledgerRevision(run) + 1 };
}

/** Seal still-running trace segments as `aborted` when the run is terminated
 * without its worker: the expired claim generation on crash recovery, or every
 * claim generation when the build itself is terminated. */
export function abortRunningTraceSegments(
  run: MeasuredBuildExecutionV1,
  generation: number | undefined | 'every-claim',
  now: number,
): MeasuredBuildTraceSegmentV1[] {
  return run.traceSegments.map(row => row.status === 'running'
    && (generation === 'every-claim' || row.claimGeneration === generation)
    ? { ...row, status: 'aborted' as const, finishedAt: now }
    : row);
}

/** Rev2 is bounded by a 20-minute end-to-end wall. A legacy 15-minute claim
 * lease would consume the recovery window before the scheduler could reclaim
 * a crashed worker. Five-minute lease: a composed build's native verb proofs
 * and proof seeds run on the worker's event loop, and a versus game simulates
 * its rival bot on every tick (Rocket Ball's verb proofs take ~6 s locally,
 * ~40 s in production, more than once per build): a one-minute lease let the
 * reaper fail a healthy build whose five-second heartbeat timer was starved
 * (v7, first beat at 63 s). Recovery still lands within lease + the 5-minute
 * reaper period, inside the 20-minute wall. */
export const MEASURED_BUILD_LEASE_MS = 5 * 60_000;

/** Fields to enqueue a new build job. */
export interface EnqueueBuildJobInput {
  jobId: string;
  creationId: string;
  version: number;
  requiredWorkerCapability?: BuildJob['requiredWorkerCapability'];
  measuredExecution?: MeasuredBuildExecutionV1;
}

/**
 * The `buildJobs/{jobId}` doc for a fresh QUEUED job — the ONE shape, shared by
 * {@link BuildJobsRepo.enqueue} and `CreationsRepo.createFork` (which enqueues
 * the fork's copy build inside the fork's birth batch).
 */
export function queuedBuildJobWrite(
  input: EnqueueBuildJobInput,
  ts: ServerTimestampSentinel,
): Record<string, unknown> {
  requireCompositionBuildJob(input);
  return {
    creationId: input.creationId,
    version: input.version,
    status: 'queued',
    workerId: null,
    claimToken: null,
    leaseGeneration: 0,
    leaseExpiry: null,
    heartbeatAt: null,
    queueEnteredAt: ts,
    claimedAt: null,
    queueIntervals: [],
    queueMs: 0,
    createdAt: ts,
    finishedAt: null,
    error: null,
    attempts: 0,
    ...(input.requiredWorkerCapability ? { requiredWorkerCapability: input.requiredWorkerCapability } : {}),
    ...(input.measuredExecution ? { measuredExecution: structuredClone(input.measuredExecution) } : {}),
  };
}

export class BuildJobsRepo {
  constructor(
    private readonly store: DocStorePort & Partial<DocTxRunner>,
    /** Injected clock for heartbeat comparisons in tests. */
    private readonly now: () => number = () => Date.now(),
    private readonly workerCapabilities: ReadonlySet<string> = new Set(),
    private readonly claimToken: () => string = () => globalThis.crypto.randomUUID(),
    private readonly leaseMs = STALE_HEARTBEAT_MS,
  ) {}

  /**
   * Enqueue a build job in the `queued` state. Throws
   * `PlatformError('conflict')` if the id already exists.
   */
  async enqueue(input: EnqueueBuildJobInput): Promise<BuildJob> {
    if (await this.store.getDoc<BuildJob>(buildJobDoc(input.jobId))) {
      throw new PlatformError('conflict', `build job ${input.jobId} already exists`);
    }
    await this.store.setDoc(
      buildJobDoc(input.jobId),
      queuedBuildJobWrite(input, this.store.serverTimestamp()),
    );
    return (await this.get(input.jobId))!;
  }

  /** Fetch a job, or null. */
  async get(jobId: string): Promise<BuildJob | null> {
    return this.store.getDoc<BuildJob>(buildJobDoc(jobId));
  }

  /** Start the revision-2 measured control plane under the exact live claim.
   * Retried delivery observes the identical retained authority. */
  async initializeMeasuredExecution(
    jobId: string,
    claim: BuildClaimAuthority,
    limits: MeasuredBuildExecutionV1['limits'],
    recipeRevision: 2 | 3 | 4 | 5 = 2,
  ): Promise<MeasuredBuildExecutionV1> {
    const job = await this.get(jobId);
    if (!job) throw new PlatformError('not-found', `measured execution: job ${jobId} not found`);
    if (job.measuredExecution) {
      // PostgreSQL stores documents as JSONB, whose object-key order is not an
      // identity guarantee. Compare the accepted values canonically so a
      // read-after-write cannot reject semantically identical limits merely
      // because JSONB returned their keys in a different order.
      if (canonicalJson(job.measuredExecution.limits) !== canonicalJson(limits)
        || Number(job.measuredExecution.recipe.revision) !== recipeRevision) {
        throw new PlatformError('conflict', 'accepted measured execution authority changed');
      }
      await this.heartbeatMeasuredClaim(jobId, claim);
      if (job.measuredExecution.compositionDigest && !job.measuredExecution.compositionV2Acceptance) {
        return startCompositionWindow(this.store, this.now, jobId, claim);
      }
      return structuredClone(job.measuredExecution);
    }
    const acceptedAt = job.createdAt;
    const execution = createMeasuredBuildExecution(acceptedAt, limits, recipeRevision);
    const won = await this.store.guardedUpdate<BuildJob>(
      buildJobDoc(jobId),
      current => this.ownsClaim(current, claim) && !current?.measuredExecution,
      { measuredExecution: execution, leaseExpiry: this.now() + MEASURED_BUILD_LEASE_MS },
    );
    if (!won) {
      const retained = await this.get(jobId);
      if (retained?.measuredExecution) return structuredClone(retained.measuredExecution);
      throw new PlatformError('conflict', `measured execution: job ${jobId} claim generation lost`);
    }
    return execution;
  }

  /** Reserve call/cost/time before an operation starts. The same reservation id
   * is idempotent only for the exact same immutable intent. */
  async reserveMeasuredOperation(
    jobId: string,
    claim: BuildClaimAuthority,
    input: ReserveMeasuredOperationInput,
    retries = 4,
  ): Promise<MeasuredBuildReservationV1> {
    if (!Number.isInteger(input.calls) || input.calls < 0 || !Number.isSafeInteger(input.costMicros) || input.costMicros < 0
      || !Number.isSafeInteger(input.maxDurationMs) || input.maxDurationMs < 1) {
      throw new PlatformError('validation', 'invalid measured operation reservation');
    }
    const job = await this.get(jobId);
    const run = job?.measuredExecution;
    if (!job || !run) throw new PlatformError('conflict', 'measured execution authority is unavailable');
    if (run.compositionDigest) {
      const deterministicKinds: ReadonlySet<MeasuredBuildOperationKind> = new Set([
        'seed', 'prepare', 'gate', 'candidate-audit', 'publication-audit', 'checkpoint', 'publish',
      ]);
      if (!deterministicKinds.has(input.kind)
        || input.calls !== 0
        || input.costMicros !== 0
        || input.provider !== undefined
        || input.model !== undefined
        || input.requestId !== undefined) {
        throw new PlatformError('validation', 'Composition code execution has no provider or media authority');
      }
    }
    const existing = run.reservations[input.reservationId];
    const identity = (r: MeasuredBuildReservationV1) => ({
      operationId: r.operationId, kind: r.kind, provider: r.provider, model: r.model,
      requestId: r.requestId, reservedCalls: r.reservedCalls,
      reservedCostMicros: r.reservedCostMicros, maxDurationMs: r.maxDurationMs,
    });
    const proposedIdentity = {
      operationId: input.operationId, kind: input.kind, provider: input.provider,
      model: input.model, requestId: input.requestId, reservedCalls: input.calls,
      reservedCostMicros: input.costMicros, maxDurationMs: input.maxDurationMs,
    };
    if (existing) {
      if (JSON.stringify(identity(existing)) !== JSON.stringify(proposedIdentity)) {
        throw new PlatformError('conflict', 'measured reservation id was reused');
      }
      return structuredClone(existing);
    }
    const now = this.now();
    if (now + input.maxDurationMs > run.deadlineAt - run.limits.finishReserveMs) {
      throw new PlatformError('unavailable', 'measured build has insufficient wall time for this operation', { retryable: false });
    }
    if (run.usage.reservedProviderCalls + input.calls > run.limits.maxProviderCalls
      || run.usage.reservedCostMicros + input.costMicros > run.limits.maxCostMicros) {
      throw new PlatformError('quota', `measured build budget exhausted: provider calls ${run.usage.reservedProviderCalls} + ${input.calls} / ${run.limits.maxProviderCalls}; reserved cost ${run.usage.reservedCostMicros} + ${input.costMicros} / ${run.limits.maxCostMicros} micros`, { retryable: false });
    }
    const reservation: MeasuredBuildReservationV1 = {
      reservationId: input.reservationId,
      operationId: input.operationId,
      kind: input.kind,
      status: 'reserved',
      ...(input.provider ? { provider: input.provider } : {}),
      ...(input.model ? { model: input.model } : {}),
      ...(input.requestId ? { requestId: input.requestId } : {}),
      reservedCalls: input.calls,
      reservedCostMicros: input.costMicros,
      maxDurationMs: input.maxDurationMs,
      reservedAt: now,
    };
    const next = advanceLedger(run, {
      usage: {
        ...run.usage,
        reservedProviderCalls: run.usage.reservedProviderCalls + input.calls,
        reservedCostMicros: run.usage.reservedCostMicros + input.costMicros,
      },
      reservations: { ...run.reservations, [input.reservationId]: reservation },
    });
    const won = await this.store.guardedUpdate<BuildJob>(
      buildJobDoc(jobId),
      current => {
        const measured = current?.measuredExecution;
        return this.ownsClaim(current, claim)
          && !!measured
          && ledgerRevision(measured) === ledgerRevision(run)
          && measured.reservations[input.reservationId] === undefined
          && measured.usage.reservedProviderCalls === run.usage.reservedProviderCalls
          && measured.usage.reservedCostMicros === run.usage.reservedCostMicros;
      },
      { measuredExecution: next },
    );
    if (!won) {
      const current = await this.get(jobId);
      if (retries > 0 && this.ownsClaim(current, claim)) {
        return this.reserveMeasuredOperation(jobId, claim, input, retries - 1);
      }
      throw new PlatformError('conflict', 'measured reservation lost claim or raced another operation');
    }
    return reservation;
  }

  async settleMeasuredOperation(
    jobId: string,
    claim: BuildClaimAuthority,
    reservationId: string,
    actualCostMicros: number,
    retries = 4,
  ): Promise<void> {
    if (!Number.isSafeInteger(actualCostMicros) || actualCostMicros < 0) throw new PlatformError('validation', 'invalid measured settlement');
    const job = await this.get(jobId);
    const run = job?.measuredExecution;
    const reservation = run?.reservations[reservationId];
    if (!run || !reservation) throw new PlatformError('not-found', 'measured reservation not found');
    if (reservation.status === 'settled') {
      if (reservation.actualCostMicros !== actualCostMicros) throw new PlatformError('conflict', 'measured settlement changed');
      return;
    }
    if (reservation.status === 'uncertain') throw new PlatformError('conflict', 'uncertain measured reservation cannot be rewritten');
    const charged = Math.max(actualCostMicros, 0);
    if (charged > reservation.reservedCostMicros) throw new PlatformError('conflict', 'provider cost exceeded its durable reservation');
    const nextReservation: MeasuredBuildReservationV1 = {
      ...reservation, status: 'settled', actualCostMicros: charged, settledAt: this.now(),
    };
    const next = advanceLedger(run, {
      usage: {
        ...run.usage,
        settledProviderCalls: run.usage.settledProviderCalls + reservation.reservedCalls,
        settledCostMicros: run.usage.settledCostMicros + charged,
        reservedCostMicros: run.usage.reservedCostMicros - reservation.reservedCostMicros + charged,
      },
      reservations: { ...run.reservations, [reservationId]: nextReservation },
    });
    const won = await this.store.guardedUpdate<BuildJob>(
      buildJobDoc(jobId),
      current => this.ownsClaim(current, claim)
        && !!current?.measuredExecution
        && ledgerRevision(current.measuredExecution) === ledgerRevision(run)
        && current?.measuredExecution?.reservations[reservationId]?.status === 'reserved',
      { measuredExecution: next },
    );
    if (!won) {
      const current = await this.get(jobId);
      if (retries > 0 && this.ownsClaim(current, claim)) {
        return this.settleMeasuredOperation(jobId, claim, reservationId, actualCostMicros, retries - 1);
      }
      throw new PlatformError('conflict', 'measured settlement lost claim generation');
    }
  }

  async markMeasuredOperationUncertain(
    jobId: string,
    claim: BuildClaimAuthority,
    reservationId: string,
    error: string,
    retries = 4,
  ): Promise<void> {
    const job = await this.get(jobId);
    const run = job?.measuredExecution;
    const reservation = run?.reservations[reservationId];
    if (!run || !reservation) throw new PlatformError('not-found', 'measured reservation not found');
    if (reservation.status === 'uncertain') return;
    if (reservation.status === 'settled') throw new PlatformError('conflict', 'settled measured reservation cannot become uncertain');
    const next = advanceLedger(run, {
      reservations: {
        ...run.reservations,
        [reservationId]: { ...reservation, status: 'uncertain', settledAt: this.now(), error: error.slice(0, 500) },
      },
    });
    const won = await this.store.guardedUpdate<BuildJob>(buildJobDoc(jobId),
      current => this.ownsClaim(current, claim)
        && !!current?.measuredExecution
        && ledgerRevision(current.measuredExecution) === ledgerRevision(run)
        && current?.measuredExecution?.reservations[reservationId]?.status === 'reserved',
      { measuredExecution: next });
    if (!won) {
      const current = await this.get(jobId);
      if (retries > 0 && this.ownsClaim(current, claim)) {
        return this.markMeasuredOperationUncertain(jobId, claim, reservationId, error, retries - 1);
      }
      throw new PlatformError('conflict', 'measured uncertain marker lost claim generation');
    }
  }

  async startMeasuredTraceSegment(jobId: string, claim: BuildClaimAuthority, retries = 4): Promise<number> {
    const job = await this.get(jobId);
    const run = job?.measuredExecution;
    if (!run) throw new PlatformError('conflict', 'measured execution authority is unavailable');
    const generation = claim.leaseGeneration ?? 0;
    const retained = run.traceSegments.find(segment => segment.claimGeneration === generation);
    if (retained) return retained.segment;
    const segment = run.traceSegments.reduce((max, row) => Math.max(max, row.segment), -1) + 1;
    const row: MeasuredBuildTraceSegmentV1 = {
      segment, claimGeneration: generation, uri: '', status: 'running', startedAt: this.now(), finishedAt: null,
    };
    const next = advanceLedger(run, { traceSegments: [...run.traceSegments, row] });
    const won = await this.store.guardedUpdate<BuildJob>(buildJobDoc(jobId),
      current => this.ownsClaim(current, claim)
        && !!current?.measuredExecution
        && ledgerRevision(current.measuredExecution) === ledgerRevision(run)
        && !current?.measuredExecution?.traceSegments.some(item => item.claimGeneration === generation),
      { measuredExecution: next });
    if (!won) {
      const current = await this.get(jobId);
      const found = current?.measuredExecution?.traceSegments.find(item => item.claimGeneration === generation);
      if (found) return found.segment;
      if (retries > 0 && this.ownsClaim(current, claim)) {
        return this.startMeasuredTraceSegment(jobId, claim, retries - 1);
      }
      throw new PlatformError('conflict', 'measured trace segment lost claim generation');
    }
    return segment;
  }

  /** Seal the exact claim's trace segment before the job becomes terminal.
   * Segment URIs are disjoint across claims and each points to that segment's
   * final full trace snapshot. */
  async finishMeasuredTraceSegment(input: {
    jobId: string;
    claim: BuildClaimAuthority;
    segment: number;
    traceUri: string;
    status: 'live' | 'failed' | 'aborted';
  }, retries = 4): Promise<void> {
    const job = await this.get(input.jobId);
    const run = job?.measuredExecution;
    const row = run?.traceSegments.find(candidate => candidate.segment === input.segment);
    if (!run || !row) throw new PlatformError('not-found', 'measured trace segment not found');
    if (row.claimGeneration !== input.claim.leaseGeneration) {
      throw new PlatformError('conflict', 'measured trace segment belongs to another claim');
    }
    if (row.status !== 'running') {
      if (row.status === input.status && row.uri === input.traceUri) return;
      throw new PlatformError('conflict', 'measured trace segment is already sealed');
    }
    const next = advanceLedger(run, {
      traceSegments: run.traceSegments.map(candidate => candidate.segment === input.segment
        ? { ...candidate, uri: input.traceUri, status: input.status, finishedAt: this.now() }
        : candidate),
    });
    const won = await this.store.guardedUpdate<BuildJob>(
      buildJobDoc(input.jobId),
      current => this.ownsClaim(current, input.claim)
        && !!current?.measuredExecution
        && ledgerRevision(current.measuredExecution) === ledgerRevision(run)
        && (current?.measuredExecution?.traceSegments.some(candidate =>
          candidate.segment === input.segment
          && candidate.claimGeneration === input.claim.leaseGeneration
          && candidate.status === 'running') ?? false),
      { measuredExecution: next },
    );
    if (!won) {
      const current = await this.get(input.jobId);
      if (retries > 0 && this.ownsClaim(current, input.claim)) {
        return this.finishMeasuredTraceSegment(input, retries - 1);
      }
      throw new PlatformError('conflict', 'measured trace finalization lost claim generation');
    }
  }

  async commitCompositionFinalProof(input: CommitCompositionProofInput) {
    return commitCompositionFinalProof(this.store, this.now, input);
  }

  async commitCompositionProof(input: CommitCompositionProofInput) {
    return commitCompositionProofBarrier(this.store, this.now, input);
  }

  async saveMeasuredCheckpoint(input: {
    jobId: string;
    claim: BuildClaimAuthority;
    executionState: Record<string, unknown>;
    candidate: MeasuredBuildCandidateV1;
    retainAsBest: boolean;
  }, retries = 4): Promise<MeasuredBuildCheckpointV1> {
    const job = await this.get(input.jobId);
    const run = job?.measuredExecution;
    if (!run) throw new PlatformError('conflict', 'measured execution authority is unavailable');
    const prior = run.checkpoint;
    const requestedState = structuredClone(input.executionState);
    delete requestedState.checkpointRevision;
    delete requestedState.currentCandidateId;
    delete requestedState.bestCandidateId;
    const retainedState = prior ? structuredClone(prior.executionState) : null;
    if (retainedState) {
      delete retainedState.checkpointRevision;
      delete retainedState.currentCandidateId;
      delete retainedState.bestCandidateId;
    }
    const requestedBestCandidateId = input.retainAsBest
      ? input.candidate.candidateId
      : (prior?.bestCandidateId ?? null);
    // A write may have committed even if its response was lost. Replaying that
    // exact milestone must return the retained revision instead of manufacturing
    // another checkpoint and another continuation identity.
    if (prior
      && prior.currentCandidateId === input.candidate.candidateId
      && prior.bestCandidateId === requestedBestCandidateId
      && canonicalJson(prior.candidates[input.candidate.candidateId] ?? null) === canonicalJson(input.candidate)
      && canonicalJson(retainedState) === canonicalJson(requestedState)) {
      await this.heartbeatMeasuredClaim(input.jobId, input.claim);
      return structuredClone(prior);
    }
    const revision = (run.checkpoint?.revision ?? 0) + 1;
    const state = structuredClone(input.executionState);
    state.checkpointRevision = revision;
    state.currentCandidateId = input.candidate.candidateId;
    state.bestCandidateId = requestedBestCandidateId;
    const checkpoint: MeasuredBuildCheckpointV1 = {
      schemaVersion: 1,
      revision,
      savedAt: this.now(),
      executionState: state,
      currentCandidateId: input.candidate.candidateId,
      bestCandidateId: input.retainAsBest
        ? input.candidate.candidateId
        : (run.checkpoint?.bestCandidateId ?? null),
      candidates: {
        ...(prior?.candidates ?? {}),
        [input.candidate.candidateId]: structuredClone(input.candidate),
      },
    };
    const won = await this.store.guardedUpdate<BuildJob>(buildJobDoc(input.jobId),
      current => this.ownsClaim(current, input.claim)
        && !!current?.measuredExecution
        && ledgerRevision(current.measuredExecution) === ledgerRevision(run)
        && (current?.measuredExecution?.checkpoint?.revision ?? 0) === revision - 1,
      { measuredExecution: advanceLedger(run, { checkpoint }) });
    if (!won) {
      const current = await this.get(input.jobId);
      if (retries > 0 && this.ownsClaim(current, input.claim)
        && (current?.measuredExecution?.checkpoint?.revision ?? 0) === revision - 1) {
        return this.saveMeasuredCheckpoint(input, retries - 1);
      }
      throw new PlatformError('conflict', 'measured checkpoint lost claim generation or revision');
    }
    return checkpoint;
  }

  async selectMeasuredCheckpoint(input: {
    jobId: string;
    claim: BuildClaimAuthority;
    candidateId: string;
    executionState: Record<string, unknown>;
  }, retries = 4): Promise<MeasuredBuildCheckpointV1> {
    const job = await this.get(input.jobId);
    const run = job?.measuredExecution;
    const prior = run?.checkpoint;
    if (!run || !prior || !prior.candidates[input.candidateId]) {
      throw new PlatformError('not-found', 'measured checkpoint candidate not found');
    }
    const revision = prior.revision + 1;
    const state = structuredClone(input.executionState);
    const requestedState = structuredClone(input.executionState);
    delete requestedState.checkpointRevision;
    delete requestedState.currentCandidateId;
    delete requestedState.bestCandidateId;
    const retainedState = structuredClone(prior.executionState);
    delete retainedState.checkpointRevision;
    delete retainedState.currentCandidateId;
    delete retainedState.bestCandidateId;
    if (prior.currentCandidateId === input.candidateId
      && canonicalJson(retainedState) === canonicalJson(requestedState)) {
      await this.heartbeatMeasuredClaim(input.jobId, input.claim);
      return structuredClone(prior);
    }
    state.checkpointRevision = revision;
    state.currentCandidateId = input.candidateId;
    state.bestCandidateId = prior.bestCandidateId;
    const checkpoint: MeasuredBuildCheckpointV1 = {
      ...prior, revision, savedAt: this.now(), executionState: state,
      currentCandidateId: input.candidateId,
    };
    const won = await this.store.guardedUpdate<BuildJob>(buildJobDoc(input.jobId),
      current => this.ownsClaim(current, input.claim)
        && !!current?.measuredExecution
        && ledgerRevision(current.measuredExecution) === ledgerRevision(run)
        && current?.measuredExecution?.checkpoint?.revision === prior.revision,
      { measuredExecution: advanceLedger(run, { checkpoint }) });
    if (!won) {
      const current = await this.get(input.jobId);
      if (retries > 0 && this.ownsClaim(current, input.claim)
        && current?.measuredExecution?.checkpoint?.revision === prior.revision) {
        return this.selectMeasuredCheckpoint(input, retries - 1);
      }
      throw new PlatformError('conflict', 'measured checkpoint selection lost claim generation');
    }
    return checkpoint;
  }

  /** Atomically retain the exact resumable checkpoint and yield the claim. */
  async yieldMeasuredClaim(input: {
    jobId: string;
    claim: BuildClaimAuthority;
    checkpoint: MeasuredBuildCheckpointV1;
    segment: number;
    traceUri: string;
  }, retries = 4): Promise<BuildJob> {
    const job = await this.get(input.jobId);
    const run = job?.measuredExecution;
    if (!job || !run) throw new PlatformError('conflict', 'measured execution authority is unavailable');
    const priorRevision = run.checkpoint?.revision ?? 0;
    if (input.checkpoint.revision !== priorRevision) throw new PlatformError('conflict', 'measured yield checkpoint is stale');
    const yielded = { ...run, checkpoint: input.checkpoint };
    if (!measuredRunProgressed(yielded)) {
      throw new PlatformError('validation', 'measured yield retained no evidence beyond the checkpoint it resumed from');
    }
    const intentId = `${input.jobId}:${input.checkpoint.revision}`;
    const traceSegments = run.traceSegments.map(row => row.segment === input.segment
      ? { ...row, uri: input.traceUri, status: 'yielded' as const, finishedAt: this.now() }
      : row);
    const next = advanceLedger(run, {
      checkpoint: structuredClone(input.checkpoint),
      traceSegments,
      continuation: { intentId, checkpointRevision: input.checkpoint.revision, progress: measuredCheckpointProgress(yielded),
        state: 'needed', createdAt: this.now() },
    });
    const won = await this.store.guardedUpdate<BuildJob>(buildJobDoc(input.jobId),
      current => this.ownsClaim(current, input.claim)
        && !!current?.measuredExecution
        && ledgerRevision(current.measuredExecution) === ledgerRevision(run)
        && current?.measuredExecution?.checkpoint?.revision === priorRevision,
      {
        measuredExecution: next,
        status: 'queued', workerId: null, claimToken: null, leaseExpiry: null,
        heartbeatAt: null, claimedAt: null, queueEnteredAt: this.store.serverTimestamp(),
        finishedAt: null, error: null,
      });
    if (!won) {
      const current = await this.get(input.jobId);
      if (retries > 0 && this.ownsClaim(current, input.claim)
        && current?.measuredExecution?.checkpoint?.revision === priorRevision) {
        return this.yieldMeasuredClaim(input, retries - 1);
      }
      throw new PlatformError('conflict', 'measured yield lost claim generation');
    }
    return (await this.get(input.jobId))!;
  }

  async getMeasuredRunForVersion(
    creationId: string,
    version: number,
  ): Promise<{ jobId: string; run: MeasuredBuildExecutionV1 } | null> {
    const rows = await this.store.query<BuildJob>(buildJobsCollection(), {
      where: [['creationId', '==', creationId], ['version', '==', version]],
      limit: 2,
    });
    const row = rows.find(candidate => candidate.measuredExecution);
    if (!row?.measuredExecution) return null;
    return { jobId: row.id, run: structuredClone(row.measuredExecution) };
  }

  async getMeasuredTraceManifest(creationId: string, version: number): Promise<MeasuredBuildTraceSegmentV1[]> {
    return structuredClone((await this.getMeasuredRunForVersion(creationId, version))?.run.traceSegments ?? []);
  }

  async getMeasuredCandidate(
    creationId: string,
    version: number,
    candidateId: string,
  ): Promise<{ jobId: string; candidate: MeasuredBuildCandidateV1; traceSegments: MeasuredBuildTraceSegmentV1[] } | null> {
    const retained = await this.getMeasuredRunForVersion(creationId, version);
    const candidate = retained?.run.checkpoint?.candidates[candidateId];
    if (!retained || !candidate) return null;
    return { jobId: retained.jobId, candidate: structuredClone(candidate), traceSegments: structuredClone(retained.run.traceSegments) };
  }

  async listPendingMeasuredContinuations(): Promise<Array<BuildJob & { id: string }>> {
    const queued = await this.store.query<BuildJob>(buildJobsCollection(), { where: [['status', '==', 'queued']] });
    return queued.filter(job => job.measuredExecution?.continuation?.state === 'needed');
  }

  async markMeasuredContinuationDispatched(jobId: string, intentId: string, retries = 4): Promise<void> {
    const job = await this.get(jobId);
    const run = job?.measuredExecution;
    const intent = run?.continuation;
    if (!run || !intent || intent.intentId !== intentId) throw new PlatformError('conflict', 'measured continuation intent changed');
    if (intent.state === 'dispatched') return;
    const won = await this.store.guardedUpdate<BuildJob>(buildJobDoc(jobId),
      current => current !== null && current.status === 'queued'
        && !!current.measuredExecution
        && ledgerRevision(current.measuredExecution) === ledgerRevision(run)
        && current.measuredExecution?.continuation?.intentId === intentId
        && current.measuredExecution.continuation.state === 'needed',
      { measuredExecution: advanceLedger(run, {
        continuation: { ...intent, state: 'dispatched', dispatchedAt: this.now() },
      }) });
    if (!won) {
      const current = await this.get(jobId);
      if (current?.measuredExecution?.continuation?.state === 'dispatched') return;
      if (retries > 0 && current?.status === 'queued'
        && current.measuredExecution?.continuation?.intentId === intentId) {
        return this.markMeasuredContinuationDispatched(jobId, intentId, retries - 1);
      }
      throw new PlatformError('conflict', 'measured continuation dispatch raced a claim');
    }
  }

  async heartbeatMeasuredClaim(jobId: string, claim: BuildClaimAuthority): Promise<void> {
    const now = this.now();
    const won = await this.store.guardedUpdate<BuildJob>(
      buildJobDoc(jobId),
      current => this.ownsClaim(current, claim) && !!current?.measuredExecution,
      { heartbeatAt: this.store.serverTimestamp(), leaseExpiry: now + MEASURED_BUILD_LEASE_MS },
    );
    if (!won) throw new PlatformError('conflict', `heartbeat: measured job ${jobId} claim generation lost`);
  }

  /** Reaper-only measured crash recovery. Resume only from a durable source
   * checkpoint; otherwise fail instead of repeating an uncheckpointed initial
   * author call whose provider outcome may be unknown. */
  async recoverExpiredMeasuredClaim(
    jobId: string,
    claim: Pick<BuildJob, 'claimToken' | 'leaseGeneration'>,
    reason = 'measured build worker unresponsive',
    retries = 4,
  ): Promise<BuildJob> {
    const now = this.now();
    const job = await this.get(jobId);
    const run = job?.measuredExecution;
    if (!job || !run) throw new PlatformError('not-found', `measured recovery: job ${jobId} not found`);
    const checkpoint = run.checkpoint;
    const checkpointState = checkpoint?.executionState;
    const checkpointIsConsistent = !!checkpoint
      && checkpoint.candidates[checkpoint.currentCandidateId]?.candidateId === checkpoint.currentCandidateId
      && checkpointState?.checkpointRevision === checkpoint.revision
      && checkpointState?.currentCandidateId === checkpoint.currentCandidateId
      && (checkpointState.bestCandidateId ?? null) === checkpoint.bestCandidateId
      && (checkpoint.bestCandidateId === null
        || checkpoint.candidates[checkpoint.bestCandidateId]?.candidateId === checkpoint.bestCandidateId);
    // Permit another automatic crash recovery only after the worker durably
    // advanced beyond the checkpoint that earned the previous recovery. This
    // preserves new work across repeated infrastructure failures without
    // creating an infinite same-checkpoint restart loop.
    const resumable = checkpointIsConsistent
      && ((job.attempts ?? 0) < 1 || measuredRunProgressed(run))
      && now < run.deadlineAt - run.limits.finishReserveMs;
    const traceSegments = abortRunningTraceSegments(run, claim.leaseGeneration, now);
    const continuation = resumable
      ? {
          intentId: `${jobId}:${run.checkpoint!.revision}`,
          checkpointRevision: run.checkpoint!.revision,
          progress: measuredCheckpointProgress(run),
          state: 'needed' as const,
          createdAt: now,
        }
      : run.continuation;
    const next = advanceLedger(run, { traceSegments, continuation });
    const won = await this.store.guardedUpdate<BuildJob>(
      buildJobDoc(jobId),
      current => current !== null
        && current.status === 'claimed'
        && !!current.measuredExecution
        && ledgerRevision(current.measuredExecution) === ledgerRevision(run)
        && current.claimToken === claim.claimToken
        && current.leaseGeneration === claim.leaseGeneration
        && (current.leaseExpiry ?? Number.MAX_SAFE_INTEGER) <= now,
      resumable
        ? {
            measuredExecution: next,
            status: 'queued', workerId: null, claimToken: null, leaseExpiry: null,
            heartbeatAt: null, claimedAt: null, queueEnteredAt: this.store.serverTimestamp(),
            finishedAt: null, error: null, attempts: (job.attempts ?? 0) + 1,
          }
        : {
            measuredExecution: next,
            status: 'failed', claimToken: null, leaseExpiry: null,
            error: reason, finishedAt: this.store.serverTimestamp(),
          },
    );
    if (!won) {
      const current = await this.get(jobId);
      if (retries > 0 && current?.status === 'claimed'
        && current.claimToken === claim.claimToken
        && current.leaseGeneration === claim.leaseGeneration) {
        return this.recoverExpiredMeasuredClaim(jobId, claim, reason, retries - 1);
      }
      throw new PlatformError('conflict', 'measured claim changed before crash recovery');
    }
    return (await this.get(jobId))!;
  }

  private ownsClaim(current: BuildJob | null, claim: BuildClaimAuthority): boolean {
    return current !== null && current.status === 'claimed'
      && current.workerId === claim.workerId
      && current.claimToken === claim.claimToken
      && current.leaseGeneration === claim.leaseGeneration
      && (current.leaseExpiry ?? 0) > this.now();
  }

  /**
   * Claim a specific queued job for `workerId` — a race-free compare-and-set on
   * `status == 'queued'` ({@link DocStorePort.guardedUpdate}, one transaction).
   * Returns the claimed job, or null when the job is gone or a concurrent
   * worker already claimed it (the loser's guard fails). No worker can win a
   * job another already owns.
   */
  async claim(jobId: string, workerId: string): Promise<(BuildJob & { id: string }) | null> {
    const current = await this.get(jobId);
    if (!current || current.status !== 'queued') return null;
    if (current.requiredWorkerCapability && !this.workerCapabilities.has(current.requiredWorkerCapability)) return null;
    const claimedAt = this.now();
    const claimToken = this.claimToken();
    const leaseGeneration = (current.leaseGeneration ?? 0) + 1;
    // Measured authority is already present on the atomically accepted queued
    // job. Give its very first claim the short measured lease too; otherwise a
    // failure before initializeMeasuredExecution() can refresh the lease leaves
    // the UI wedged behind the legacy fifteen-minute recovery window.
    const leaseExpiry = claimedAt + (current.measuredExecution ? MEASURED_BUILD_LEASE_MS : this.leaseMs);
    const enteredAt = current.queueEnteredAt ?? current.createdAt ?? claimedAt;
    const queueIntervals = [
      ...(current.queueIntervals ?? []),
      { enteredAt, claimedAt },
    ];
    const queueMs = queueIntervals.reduce(
      (sum, interval) => sum + Math.max(0, (interval.claimedAt ?? claimedAt) - interval.enteredAt),
      0,
    );
    const won = await this.store.guardedUpdate<BuildJob>(
      buildJobDoc(jobId),
      (job) => job !== null && job.status === 'queued',
      {
        status: 'claimed',
        workerId,
        claimToken,
        leaseGeneration,
        leaseExpiry,
        heartbeatAt: this.store.serverTimestamp(),
        claimedAt,
        queueEnteredAt: null,
        queueIntervals,
        queueMs,
      },
    );
    if (!won) return null;
    const claimed = await this.get(jobId);
    if (!claimed) return null;
    // Attach the doc id so a polling worker (claimNext) can address the job for
    // heartbeat/complete/fail without a separate lookup.
    return { ...claimed, id: jobId };
  }

  /**
   * Claim the OLDEST queued job for `workerId`, or null when the queue is empty.
   * Convenience over {@link claim} for a polling worker.
   */
  async claimNext(workerId: string): Promise<(BuildJob & { id: string }) | null> {
    const spec: QuerySpec = {
      where: [['status', '==', 'queued']],
      orderBy: [['createdAt', 'asc']],
      limit: 1,
    };
    const queued = await this.store.query<BuildJob>(buildJobsCollection(), { ...spec, limit: 25 });
    for (const candidate of queued) {
      if (candidate.requiredWorkerCapability && !this.workerCapabilities.has(candidate.requiredWorkerCapability)) continue;
      const claimed = await this.claim(candidate.id, workerId);
      if (claimed) return claimed;
    }
    return null;
  }

  /**
   * Refresh a claimed job's heartbeat. Throws `PlatformError('conflict')` if the
   * job isn't claimed by `workerId` (a stolen/expired claim).
   */
  async heartbeat(jobId: string, workerId: string): Promise<void> {
    const job = await this.get(jobId);
    if (!job) throw new PlatformError('not-found', `heartbeat: job ${jobId} not found`);
    if (requiresClaimGeneration(job)) {
      throw new PlatformError('conflict', `${job.requiredWorkerCapability} heartbeat requires claim-generation authority`);
    }
    if (job.status !== 'claimed' || job.workerId !== workerId) {
      throw new PlatformError(
        'conflict',
        `heartbeat: job ${jobId} not claimed by ${workerId} (status ${job.status}, worker ${job.workerId})`,
      );
    }
    await this.store.update(buildJobDoc(jobId), {
      heartbeatAt: this.store.serverTimestamp(),
      leaseExpiry: this.now() + this.leaseMs,
    });
  }

  /** Claim-generation-fenced heartbeat for verified-core@2 consumers. */
  async heartbeatClaim(jobId: string, claim: Pick<BuildJob, 'workerId' | 'claimToken' | 'leaseGeneration'>): Promise<void> {
    const now = this.now();
    const won = await this.store.guardedUpdate<BuildJob>(
      buildJobDoc(jobId),
      job => job !== null && job.status === 'claimed'
        && job.workerId === claim.workerId
        && job.claimToken === claim.claimToken
        && job.leaseGeneration === claim.leaseGeneration
        && (job.leaseExpiry ?? 0) > now,
      { heartbeatAt: this.store.serverTimestamp(), leaseExpiry: now + this.leaseMs },
    );
    if (!won) throw new PlatformError('conflict', `heartbeat: job ${jobId} claim generation lost`);
  }

  /**
   * Mark a claimed job done. Only the owning `workerId` may complete it. Sets
   * `finishedAt`.
   */
  async complete(jobId: string, workerId: string): Promise<void> {
    await this.terminate(jobId, workerId, 'done', null);
  }

  /**
   * Mark a claimed job failed with a reason. Only the owning `workerId` may fail
   * it. Sets `finishedAt` and `error`.
   */
  async fail(jobId: string, workerId: string, error: string): Promise<void> {
    await this.terminate(jobId, workerId, 'failed', error);
  }

  async completeClaim(jobId: string, claim: Pick<BuildJob, 'workerId' | 'claimToken' | 'leaseGeneration'>): Promise<void> {
    await this.terminateClaim(jobId, claim, 'done', null);
  }

  async failClaim(jobId: string, claim: Pick<BuildJob, 'workerId' | 'claimToken' | 'leaseGeneration'>, error: string): Promise<void> {
    await this.terminateClaim(jobId, claim, 'failed', error);
  }

  private async terminateClaim(
    jobId: string,
    claim: Pick<BuildJob, 'workerId' | 'claimToken' | 'leaseGeneration'>,
    status: 'done' | 'failed',
    error: string | null,
  ): Promise<void> {
    const now = this.now();
    const won = await this.store.guardedUpdate<BuildJob>(
      buildJobDoc(jobId),
      job => job !== null && job.status === 'claimed'
        && job.workerId === claim.workerId
        && job.claimToken === claim.claimToken
        && job.leaseGeneration === claim.leaseGeneration
        && (job.leaseExpiry ?? 0) > now,
      { status, error, finishedAt: this.store.serverTimestamp(), leaseExpiry: null },
    );
    if (!won) throw new PlatformError('conflict', `${status}: job ${jobId} claim generation lost`);
  }

  private async terminate(
    jobId: string,
    workerId: string,
    status: 'done' | 'failed',
    error: string | null,
  ): Promise<void> {
    const job = await this.get(jobId);
    if (!job) throw new PlatformError('not-found', `${status}: job ${jobId} not found`);
    if (requiresClaimGeneration(job)) {
      throw new PlatformError('conflict', `${job.requiredWorkerCapability} ${status} requires claim-generation authority`);
    }
    if (job.status !== 'claimed' || job.workerId !== workerId) {
      throw new PlatformError(
        'conflict',
        `${status}: job ${jobId} not claimed by ${workerId} (status ${job.status})`,
      );
    }
    await this.store.update(buildJobDoc(jobId), {
      status,
      error,
      finishedAt: this.store.serverTimestamp(),
    });
  }

  /**
   * How many jobs are currently CLAIMED (a build in flight somewhere). The
   * builder's admission check compares this against
   * `platformSettings.buildConcurrency` before claiming another (R3-3).
   */
  async countActive(): Promise<number> {
    const claimed = await this.store.query<BuildJob>(buildJobsCollection(), {
      where: [['status', '==', 'claimed']],
    });
    return claimed.length;
  }

  /**
   * List jobs whose heartbeat is older than `staleMs` (candidates for requeue).
   * Uses the injected clock. Only `claimed` jobs are considered.
   */
  async listStale(staleMs: number): Promise<Array<BuildJob & { id: string }>> {
    const cutoff = this.now() - staleMs;
    const claimed = await this.store.query<BuildJob>(buildJobsCollection(), {
      where: [['status', '==', 'claimed']],
    });
    return claimed.filter((j) => j.measuredExecution
      ? (j.leaseExpiry ?? 0) <= this.now()
      : (j.heartbeatAt ?? 0) < cutoff);
  }

  /**
   * List QUEUED jobs enqueued more than `staleMs` ago and NEVER claimed — the
   * starvation candidates {@link listStale} cannot see (they have no worker and
   * no heartbeat, so the claimed-job scan skips them). A build is claimed by a
   * Cloud Task POST driving {@link claimNext}, which claims the OLDEST queued
   * job — NOT necessarily the one the task was minted for — so under overlapping
   * builds a queued job can be orphaned once its own task is consumed claiming a
   * different job. With nothing left to trigger another claim it sits `queued`
   * forever. The reaper re-pushes a fresh Cloud Task for each of these (a queued
   * job is always claimable; the claim CAS + buildConcurrency admission keep a
   * re-push from double-building). Uses the injected clock; only `queued`.
   */
  async listStuckQueued(staleMs: number): Promise<Array<BuildJob & { id: string }>> {
    const cutoff = this.now() - staleMs;
    const queued = await this.store.query<BuildJob>(buildJobsCollection(), {
      where: [['status', '==', 'queued']],
    });
    return queued.filter((j) => (j.createdAt ?? 0) < cutoff);
  }

  /**
   * REQUEUE a stale/failed job back to `queued` for another worker to claim —
   * clears the claim (workerId/heartbeat) and BUMPS `attempts` so the reaper
   * requeues a given job at most once before abandoning it. Unlike {@link claim}
   * this has no owner guard: the reaper (a system actor) requeues jobs whose
   * worker is presumed dead. Returns the requeued job. Not for terminal
   * (`done`) jobs — the caller filters to non-terminal.
   */
  async requeue(jobId: string): Promise<BuildJob> {
    const job = await this.get(jobId);
    if (!job) throw new PlatformError('not-found', `requeue: job ${jobId} not found`);
    requireCompositionBuildJob(job);
    if (requiresClaimGeneration(job)) {
      throw new PlatformError('conflict', `${job.requiredWorkerCapability} requeue requires the expired claim generation`);
    }
    const queueEnteredAt = job.status === 'queued'
      ? (job.queueEnteredAt ?? this.store.serverTimestamp())
      : this.store.serverTimestamp();
    await this.store.update(buildJobDoc(jobId), {
      status: 'queued',
      workerId: null,
      claimToken: null,
      leaseExpiry: null,
      heartbeatAt: null,
      claimedAt: null,
      queueEnteredAt,
      finishedAt: null,
      error: null,
      attempts: (job.attempts ?? 0) + 1,
    });
    return (await this.get(jobId))!;
  }

  /** Reaper-only v2 recovery. It may clear exactly the expired generation it
   * observed; a renewed/reacquired lease makes the compare-and-set lose. */
  async requeueExpiredClaim(
    jobId: string,
    claim: Pick<BuildJob, 'claimToken' | 'leaseGeneration'>,
  ): Promise<BuildJob> {
    const now = this.now();
    const job = await this.get(jobId);
    if (!job) throw new PlatformError('not-found', `requeue: job ${jobId} not found`);
    requireCompositionBuildJob(job);
    const won = await this.store.guardedUpdate<BuildJob>(
      buildJobDoc(jobId),
      current => current !== null
        && !!current.requiredWorkerCapability && requiresClaimGeneration(current)
        && current.status === 'claimed'
        && current.claimToken === claim.claimToken
        && current.leaseGeneration === claim.leaseGeneration
        && (current.leaseExpiry ?? Number.MAX_SAFE_INTEGER) <= now,
      {
        status: 'queued', workerId: null, claimToken: null, leaseExpiry: null,
        heartbeatAt: null, claimedAt: null, queueEnteredAt: this.store.serverTimestamp(),
        finishedAt: null, error: null, attempts: (job.attempts ?? 0) + 1,
      },
    );
    if (!won) throw new PlatformError('conflict', 'build claim changed before v2 recovery');
    return (await this.get(jobId))!;
  }

  async abandonClaim(
    jobId: string,
    claim: Pick<BuildJob, 'claimToken' | 'leaseGeneration'>,
    reason: string,
  ): Promise<void> {
    const won = await this.store.guardedUpdate<BuildJob>(
      buildJobDoc(jobId),
      job => job !== null
        && !!job.requiredWorkerCapability && requiresClaimGeneration(job)
        && job.status !== 'done'
        && job.status !== 'failed'
        && job.claimToken === claim.claimToken
        && job.leaseGeneration === claim.leaseGeneration,
      { status: 'failed', error: reason, claimToken: null, leaseExpiry: null, finishedAt: this.store.serverTimestamp() },
    );
    if (!won) throw new PlatformError('conflict', 'build claim changed before v2 abandonment');
  }
}

/**
 * Cutoff (ms since last heartbeat) after which a CLAIMED build job counts as
 * STALE — its worker is presumed dead and the claim is safe to reap/requeue.
 * The builder's heartbeat cadence (`HEARTBEAT_MS`, 5s, in
 * `services/builder/src/worker/buildWorker.ts`) sits far under this, so a live
 * worker can never look stale. Single source of the value; consumers:
 * {@link BuildJobsRepo.listStale} callers (the `staleMs` to pass) and
 * `tools/ops/purge-test-data.mjs` (reaps stuck claimed jobs past this cutoff).
 */
export const STALE_HEARTBEAT_MS = 15 * 60 * 1000;
